import { forwardRef, useCallback, useEffect, useId, useRef, type HTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { IconButton } from "./IconButton";
import { cn } from "@/lib/cn";
import { useEscapeStack } from "./overlayStack";

// Stage 5: local replacement for @datasirpi/ui's <Modal>. Ported
// letter-for-letter from the vendor's compiled bundle (focus trap,
// initial-focus, focus-restoration, body scroll-lock, Escape-to-close,
// overlay-click-to-close, portal target) and Modal-2SWZLRZU.css.
//
// IMPORTANT — unlike every other Stage 5 component so far, this behavior
// is NOT verifiable by this repo's tooling: tsc/eslint/next build/CSS-diff
// can confirm the code compiles and the styles match, but none of them can
// exercise a live focus trap, Escape handling, or scroll-lock in a real
// browser (this repo has zero tests and no dev-backend credentials for a
// live check). Ported as faithfully as reading the source allows, but this
// is the one family in Stage 5 that still wants a manual browser
// spot-check before being trusted the way the others already have been.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type ModalSize = "sm" | "md" | "lg" | "xl";

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  dismissOnOverlay?: boolean;
  showCloseButton?: boolean;
  container?: HTMLElement | null;
  children?: ReactNode;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  { open, onClose, title, description, footer, size = "md", dismissOnOverlay = true, showCloseButton = true, container, className, children, ...rest },
  ref
) {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = description ? `${baseId}-desc` : undefined;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      dialogRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref]
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = (document.activeElement as HTMLElement) ?? null;
    return () => {
      previouslyFocused.current?.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      if (!dialogRef.current) return;
      const first = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? dialogRef.current).focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  /* Phase 4.5 — Escape goes through the shared overlay stack, so only the
     TOP-most overlay closes. The listener this replaces was bound to
     `document` and called e.stopPropagation(), which does nothing between
     listeners on the same node: every open Modal and Drawer received the
     same Escape and all of them closed at once, so a confirm dialog
     dismissed the form behind it (finding F16). */
  useEscapeStack(open, onClose);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute("disabled")
    );
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !dialogRef.current.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  function onOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (!dismissOnOverlay) return;
    if (e.target === e.currentTarget) onClose();
  }

  const portalTarget = container ?? (typeof document !== "undefined" ? document.body : null);
  if (!portalTarget) return null;

  return createPortal(
    <div className="rc-modal-overlay" onMouseDown={onOverlayMouseDown}>
      <div
        ref={setRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className={cn("rc-modal", `rc-modal--${size}`, className)}
        onKeyDown={onKeyDown}
        {...rest}
      >
        <header className="rc-modal__header">
          <div>
            <h2 id={titleId} className="rc-modal__title">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="m-[var(--ds-space-1)_0_0] text-[var(--ds-font-size-sm)] text-[var(--ds-color-text-muted)]">
                {description}
              </p>
            ) : null}
          </div>
          {showCloseButton ? (
            <IconButton aria-label="Close dialog" icon={<X />} size="sm" onClick={onClose} className="rc-modal__close" />
          ) : null}
        </header>
        <div className="rc-modal__body">{children}</div>
        {footer ? <footer className="rc-modal__footer">{footer}</footer> : null}
      </div>
    </div>,
    portalTarget
  );
});
